import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Mail, Loader2, ShieldCheck } from "lucide-react";

export default function Register() {
  const { refresh } = useAuth();
  const t = useT();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [devNote, setDevNote] = useState(null);

  const start = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error(t("toast.pwdShort"));
    setBusy(true);
    try {
      const r = await api.post("/auth/register/start", { email, password, name });
      toast.success(t("toast.codeSent", { email }));
      if (r.data?.dev_code) setDevNote(r.data);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start signup");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error(t("toast.enter6"));
    setBusy(true);
    try {
      const r = await api.post("/auth/register/verify", { email, code });
      setToken(r.data.token);
      await refresh();
      toast.success(t("toast.accountCreated"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/register/resend", { email });
      if (r.data?.dev_code) setDevNote(r.data);
      toast.success(t("toast.newCodeSent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] paper-bg grid place-items-center px-4 py-12">
      <div className="w-full max-w-md card-classic p-8" data-testid="register-page">
        {step === 1 ? (
          <>
            <h1 className="font-serif-display text-3xl font-bold text-[#0F172A]">{t("register.title")}</h1>
            <p className="text-[#475569] mt-1">{t("register.subtitle")}</p>
            <form onSubmit={start} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="name">{t("auth.name")}</Label>
                <Input id="name" value={name} onChange={(e)=>setName(e.target.value)}
                  data-testid="register-name-input" className="mt-1"/>
              </div>
              <div>
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" type="email" required value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  data-testid="register-email-input" className="mt-1"/>
              </div>
              <div>
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input id="password" type="password" required value={password}
                  onChange={(e)=>setPassword(e.target.value)}
                  data-testid="register-password-input" className="mt-1"/>
              </div>
              <Button type="submit" disabled={busy}
                className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11"
                data-testid="register-submit-button">
                {busy ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("register.sending")}</>) : t("register.sendCode")}
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 pill mb-4">
              <Mail size={12}/> {t("register.step2.badge")}
            </div>
            <h1 className="font-serif-display text-3xl font-bold text-[#0F172A]">{t("register.step2.title")}</h1>
            <p className="text-[#475569] mt-1">
              {t("register.step2.subtitle")} <strong>{email}</strong>{t("register.step2.subtitle2")}
            </p>

            {devNote?.dev_code && (
              <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900" data-testid="dev-code-banner">
                <strong>{t("register.devNote")}</strong> <span className="font-mono-jb text-base ml-1">{devNote.dev_code}</span>
              </div>
            )}

            <form onSubmit={verify} className="mt-6 space-y-5">
              <div className="flex justify-center" dir="ltr" data-testid="verify-code-otp">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => (
                      <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl font-serif-display" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button type="submit" disabled={busy || code.length !== 6}
                className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-11"
                data-testid="verify-code-submit-button">
                {busy ? (<><Loader2 size={16} className="mr-2 animate-spin"/> {t("register.step2.verifying")}</>) : (<><ShieldCheck size={16} className="mr-2"/> {t("register.step2.verify")}</>)}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => setStep(1)} className="text-[#475569] hover:text-[#0F172A]" data-testid="verify-back-button">
                  {t("register.step2.changeEmail")}
                </button>
                <button type="button" onClick={resend} disabled={busy} className="font-semibold text-[#0F172A] underline" data-testid="verify-resend-button">
                  {t("register.step2.resend")}
                </button>
              </div>
            </form>
          </>
        )}

        <p className="mt-6 text-sm text-[#475569]">
          {t("register.hasAccount")} <Link to="/login" className="font-semibold text-[#0F172A] underline">{t("register.signIn")}</Link>
        </p>
      </div>
    </div>
  );
}
